import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import posthog from 'posthog-js';
import { phrasesApi, practiceApi, analyzeAudioApi, collectionsApi, premadeApi } from '../lib/api';
import { isLessonPhrase } from '../lib/soundLessons';
import { AudioRecorder, MAX_RECORDING_MS, RECORDING_CAPPED_MESSAGE, getSavedSpeed, saveSpeed, getSavedKaraoke, measureLoudness, getMicPermissionState, useVoiceCapabilities, newRecDiag, IS_IOS, playRecording, PLAYBACK_FAILED_MESSAGE } from '../lib/speech';
import { speakFrench, cancelFrench, getSavedTTSVoice, unlockAudioPlayback } from '../lib/tts';
import { useWakeLock } from '../lib/useWakeLock';
import { reportError, feedbackShownProps, requestFailureReason } from '../lib/analytics';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import SessionHeader from '../components/SessionHeader';
import PhraseDisplay from '../components/PhraseDisplay';
import PhraseCard from '../components/PhraseCard';
import ScoreHeader from '../components/ScoreHeader';
import SessionSummary from '../components/SessionSummary';
import NextPhraseButton from '../components/NextPhraseButton';
import TryAgainButton from '../components/TryAgainButton';
import RecordingNotice from '../components/RecordingNotice';
import { UpgradeDialog } from '../components/ProUpgrade';
import { ScoredPhrase, PronunciationTips, TapWordPrompt } from '../components/PronunciationTips';
import SpeedControl from '../components/SpeedControl';
import FeedbackBox from '../components/FeedbackBox';
import { pronunciationBeta, REDO_BELOW } from '../lib/score';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CollectionOptions } from '../components/CollectionSelect';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { markPractised } from '../lib/installPrompt';
import { ArrowLeft, Volume2, Mic, MicOff, RotateCcw, ArrowRight, Check, Loader2, PlayCircle, Shuffle, Target, Folder, LogOut, Pointer, X, ChevronRight } from 'lucide-react';
import { useSwipe } from '../lib/useSwipe';

// Remembers that mic access was granted at least once on this device, so a
// later permission popup can be flagged as a repeat (iOS re-prompts across
// sessions even after granting).
const MIC_GRANTED_KEY = 'mimique_mic_granted';

const Manual = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const collectionIds = searchParams.get('collections')?.split(',').filter(Boolean) || [];
    const phraseIds = searchParams.get('phrase_ids')?.split(',').filter(Boolean) || []; // specific set (e.g. weakest phrases)
    const order = searchParams.get('order') || 'smart';          // smart | random (phrase selection)
    const singlePhraseId = searchParams.get('phrase_id') || null;
    // A sound lesson's drill, practised without adding it to their phrases
    // (Leanne, 25 Oct 2026). Its phrases have "lesson-" ids and are not saved.
    const lessonTopic = searchParams.get('lesson') || null;
    const returnTo = searchParams.get('returnTo') === 'collections' ? '/collections' : '/dashboard';
    
    const [phrases, setPhrases] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [collectionLabel, setCollectionLabel] = useState('');
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sessionSummary, setSessionSummary] = useState(null);
    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    const phrase = phrases[currentIndex] || null;
    const [isPlaying, setIsPlaying] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [result, setResult] = useState(null);
    // Where the picked-word controls get portalled to: below both
    // phrases, so opening them cannot shove either one down the page.
    const [wordFooter, setWordFooter] = useState(null);
    const [practiceCount, setPracticeCount] = useState(0);
    const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
    // The recording itself, not a url for it. Playing one word back decodes
    // these bytes, and a blob cannot go stale the way an object url can.
    const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
    // The server's 402 detail while the upgrade dialog is open, null otherwise.
    const [upgradePrompt, setUpgradePrompt] = useState(null);
    const [isPlayingRecording, setIsPlayingRecording] = useState(false);
    // 0-100 progress of the 29s recording cap — drives the on-screen bar so
    // the user can see how much of their speaking window they've used.
    const [recordingProgress, setRecordingProgress] = useState(0);
    const recordingProgressIntervalRef = useRef(null);
    // Set to true when the recorder auto-stopped at the 29s cap — flips a
    // one-time explanation under the score. Cleared when a new recording starts.
    const [autoStoppedNotice, setAutoStoppedNotice] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(getSavedSpeed);
    const [activeCharIndex, setActiveCharIndex] = useState(-1);
    const [karaokeEnabled] = useState(getSavedKaraoke);
    const { caps: voiceCaps, ready: capsReady } = useVoiceCapabilities();

    // Keep the screen on for the whole session so the OS screensaver/auto-lock
    // doesn't interrupt mid-phrase.
    useWakeLock(true);

    // Old iOS/Safari with no MediaRecorder can't record at all — track the drop-off.
    useEffect(() => {
        if (typeof MediaRecorder === 'undefined') {
            reportError('mic_unsupported',{ error_type: 'media_recorder_unsupported', mode: 'manual' });
        }
    }, []);

    const allPhrasesRef = useRef([]);
    const sessionResultsRef = useRef([]);
    const audioRecorderRef = useRef(null);
    const audioPlayerRef = useRef(null);
    const wordBoundariesRef = useRef([]);
    const highlightTimersRef = useRef([]);
    const recordingStartRef = useRef(null);
    // The mic, opened before the user taps Record. Automatic has always done
    // this (it pre-warms while the French plays), but Manual opened the mic on
    // the tap itself, and getUserMedia costs up to a second on iOS, so the
    // first word was cut off every time. The two modes behave the same.
    const preWarmRef = useRef(null);

    const streamIsLive = (rec) =>
        !!rec?.stream && rec.stream.getTracks().some(t => t.readyState === 'live');

    // Open the mic ahead of the tap, but only when permission was already
    // granted: pre-warming must never be what raises the permission popup,
    // because that popup belongs to a deliberate tap on Record.
    // Not on iPhones: a mic opened in the background there records silence,
    // so every recording after the first came back "No speech detected"
    // (20 of 42 iPhone Manual recordings from 23 Sep to 28 Oct 2026, none
    // before). iPhones open the mic on the tap, as they did before 23 Sep.
    const preWarmMic = async () => {
        if (IS_IOS) return;
        if (isRecording || streamIsLive(preWarmRef.current)) return;
        try {
            if ((await getMicPermissionState()) !== 'granted') return;
        } catch {
            return;
        }
        const rec = new AudioRecorder();
        if ((await rec.preWarm()) === true) preWarmRef.current = rec;
        else rec.cleanup();
    };

    const releasePreWarmedMic = () => {
        const rec = preWarmRef.current;
        preWarmRef.current = null;
        if (rec) rec.cleanup();
    };

    const clearHighlightTimers = () => {
        highlightTimersRef.current.forEach(t => clearTimeout(t));
        highlightTimersRef.current = [];
    };

    const replayHighlighting = (boundaries, startDelay = 0) => {
        clearHighlightTimers();
        setActiveCharIndex(-1);
        // Captured timings include the TTS engine's spin-up latency before the first
        // word, so rebase to the first word's time. startDelay adds a lead-in so the
        // user has a moment to begin speaking before the first word highlights.
        const offset = boundaries.length ? boundaries[0].elapsed : 0;
        boundaries = boundaries.map(b => ({ charIndex: b.charIndex, elapsed: Math.max(0, b.elapsed - offset) + startDelay }));
        boundaries.forEach(({ charIndex, elapsed }) => {
            const t = setTimeout(() => setActiveCharIndex(charIndex), elapsed);
            highlightTimersRef.current.push(t);
        });
    };

    useEffect(() => {
        fetchPhrases();
        collectionsApi.getAll().then(res => setCollections(res.data)).catch(() => {});
        if (!singlePhraseId && !lessonTopic) {
            if (collectionIds.length === 0) {
                setCollectionLabel('All Phrases');
            } else {
                collectionsApi.getAll().then(res => {
                    const names = collectionIds.map(id => res.data.find(c => c.id === id)?.name).filter(Boolean);
                    setCollectionLabel(names.length ? names.join(', ') : 'All Phrases');
                }).catch(() => {});
            }
        }

        return () => {
            clearHighlightTimers();
            cancelFrench();
            if (audioRecorderRef.current) audioRecorderRef.current.cleanup();
            if (recordingProgressIntervalRef.current) clearInterval(recordingProgressIntervalRef.current);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Warm the mic whenever the page is sitting on a phrase and not recording:
    // on arrival, after each recording is analysed, and on every new phrase.
    useEffect(() => {
        if (!phrase || isRecording) return;
        preWarmMic();
    }, [phrase?.id, isRecording]); // eslint-disable-line react-hooks/exhaustive-deps

    // Never hold the mic open in the background: the device's recording
    // indicator stays lit, and iOS kills the tracks anyway.
    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) releasePreWarmedMic();
            else preWarmMic();
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            releasePreWarmedMic();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Load the full ordered list up front so Manual can step through one phrase at
    // a time while still showing "Phrase X of N" and an overall progress bar.
    const fetchPhrases = async () => {
        setLoading(true);
        setResult(null);
        setRecordedAudioUrl(null);
        setRecordedAudioBlob(null);

        try {
            if (lessonTopic) {
                const res = await premadeApi.phrases(lessonTopic);
                setCollectionLabel(res.data.label || '');
                allPhrasesRef.current = res.data.phrases;
                setPhrases(res.data.phrases);
                setCurrentIndex(0);
                return;
            }
            // Single-phrase manual run (from Quick Practice): load just that phrase.
            if (singlePhraseId) {
                const res = await phrasesApi.getById(singlePhraseId);
                setPhrases([res.data]);
                setCurrentIndex(0);
                return;
            }
            // Specific set of phrases in the given order (e.g. "practise weakest").
            if (phraseIds.length > 0) {
                const res = await phrasesApi.getAll();
                const byId = new Map(res.data.map(p => [p.id, p]));
                const ordered = phraseIds.map(id => byId.get(id)).filter(Boolean);
                if (ordered.length === 0) { toast.error('No phrases found.'); navigate('/dashboard'); return; }
                allPhrasesRef.current = ordered;
                setPhrases(ordered);
                setCurrentIndex(0);
                return;
            }
            const promises = collectionIds.length > 0
                ? collectionIds.map(id => phrasesApi.getAll(id))
                : [phrasesApi.getAll()];
            const results = await Promise.all(promises);
            const allPhrases = results.flatMap(r => r.data);
            let unique = [...new Map(allPhrases.map(p => [p.id, p])).values()];
            if (unique.length === 0) {
                toast.error('No phrases found.');
                navigate('/dashboard');
                return;
            }
            if (order === 'random') {
                for (let i = unique.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [unique[i], unique[j]] = [unique[j], unique[i]];
                }
            } else if (order === 'added') {
                unique.sort((a, b) => (a.created_at || '') < (b.created_at || '') ? -1 : (a.created_at || '') > (b.created_at || '') ? 1 : 0);
            } else {
                unique.sort((a, b) => {
                    const aNever = (a.practice_count || 0) === 0;
                    const bNever = (b.practice_count || 0) === 0;
                    if (aNever !== bNever) return aNever ? -1 : 1;
                    const scoreDiff = (a.average_score || 0) - (b.average_score || 0);
                    if (scoreDiff !== 0) return scoreDiff;
                    const aLast = a.last_practiced || '';
                    const bLast = b.last_practiced || '';
                    return aLast < bLast ? -1 : aLast > bLast ? 1 : 0;
                });
            }
            allPhrasesRef.current = unique;
            setPhrases(unique);
            setCurrentIndex(0);
        } catch (err) {
            if (err.response?.status === 404) {
                toast.error('Phrase not found.');
                navigate('/dashboard');
            } else {
                toast.error('Failed to load phrase');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePlayAudio = async () => {
        if (isPlaying || !phrase) return;
        // Hands-free counts every play; manual never did, so its listens were
        // missing from the dashboard and the mode breakdown entirely.
        if (!isLessonPhrase(phrase)) practiceApi.recordListen(phrase.id, 'listen-record').catch(() => {});
        // Unlock inside the tap: the TTS fetch outlives the gesture, so without
        // this iOS refuses the natural voice and drops to the device one.
        unlockAudioPlayback();
        setIsPlaying(true);
        wordBoundariesRef.current = [];
        setActiveCharIndex(-1);
        try {
            const ttsStart = Date.now();
            const onBoundary = (charIndex) => {
                wordBoundariesRef.current.push({ charIndex, elapsed: Date.now() - ttsStart });
                setActiveCharIndex(charIndex);
            };
            await speakFrench(phrase.french, playbackSpeed, getSavedTTSVoice(), onBoundary);
            posthog.capture('phrase_listened', { phrase_id: phrase.id, source: 'practice' });
        } catch (err) {
            reportError('tts_failed', {
                error_type: 'tts_playback_failed',
                mode: 'manual',
                tts_error_code: err?.error || err?.message || 'unknown',
                voice_uri: getSavedTTSVoice() || '(default)',
            });
            toast.error(`Voice playback unavailable (${err?.error || err?.message || 'unknown'}). Try again, or install a French voice in your device settings.`);
        } finally {
            setIsPlaying(false);
            setActiveCharIndex(-1);
        }
    };

    const handleSpeedChange = (speed) => {
        setPlaybackSpeed(speed);
        saveSpeed(speed);
    };

    const handleStartRecording = async () => {
        setResult(null);
        setRecordedAudioUrl(null);
        setRecordedAudioBlob(null);

        // Track the browser's mic permission popup: state 'prompt' right before
        // getUserMedia means the popup is about to appear. Repeat popups after a
        // previous successful grant (iOS re-prompts across sessions) are the
        // interesting case, flagged via repeat_after_grant.
        const permState = await getMicPermissionState();
        const previouslyGranted = localStorage.getItem(MIC_GRANTED_KEY) === '1';
        if (permState === 'prompt') {
            posthog.capture('mic_permission_popup_shown', {
                mode: 'manual',
                repeat_after_grant: previouslyGranted,
            });
        }

        // Record from the mic that is already open when there is one. Opening
        // it here instead costs up to a second on iOS, and every word spoken in
        // that gap is lost, which is the "it misses my first word" report.
        const warm = streamIsLive(preWarmRef.current) ? preWarmRef.current : null;
        audioRecorderRef.current = warm || new AudioRecorder();
        preWarmRef.current = null;
        // Test accounts: a timeline of this recording (see newRecDiag).
        audioRecorderRef.current.diag = newRecDiag('manual');
        audioRecorderRef.current.diag?.mark('tap_record', { warm_mic: !!warm });
        // If the recorder hits its 29s hard cap before the user taps stop,
        // run the same stop-and-analyze flow so their clip isn't lost, and
        // tell the user what happened so they know why it stopped.
        audioRecorderRef.current.onAutoStopped = () => {
            setAutoStoppedNotice(true);
            toast.error(RECORDING_CAPPED_MESSAGE);
            handleStopRecording();
        };
        // A warm stream that refuses to start the recorder falls back to
        // opening the mic from scratch, so a failure here is never silent.
        const started = warm && audioRecorderRef.current.startFromStream() === true
            ? true
            : await audioRecorderRef.current.start();
        if (started === true) localStorage.setItem(MIC_GRANTED_KEY, '1');

        if (started !== true) {
            // Raw error details so a failing session self-reports the actual cause
            // (insecure context, iOS Settings-level block, webview, etc.).
            const diag = {
                mode: 'manual',
                error_name: started,
                error_message: audioRecorderRef.current?.lastErrorMessage || '',
                secure_context: window.isSecureContext,
            };
            if (started === 'NotAllowedError' || started === 'PermissionDeniedError') {
                reportError('mic_denied',{ error_type: 'permission_denied', ...diag });
                toast.error('Microphone access was denied. Go to your device Settings and allow microphone access for this browser, then try again.');
            } else if (started === 'NotFoundError' || started === 'DevicesNotFoundError') {
                reportError('mic_missing',{ error_type: 'no_microphone_found', ...diag });
                toast.error('No microphone found. Please check your device has a working microphone.');
            } else {
                reportError('mic_failed',{ error_type: 'mic_access_failed', ...diag });
                toast.error('Could not access microphone. Please check your browser and device settings.');
            }
            return;
        }

        recordingStartRef.current = Date.now();
        setIsRecording(true);
        setAutoStoppedNotice(false);
        // Drive the 29s progress bar. Cleared in handleStopRecording (either
        // when the user taps stop or when the recorder auto-stops at the cap).
        setRecordingProgress(0);
        if (recordingProgressIntervalRef.current) clearInterval(recordingProgressIntervalRef.current);
        const barStart = Date.now();
        recordingProgressIntervalRef.current = setInterval(() => {
            const pct = Math.min(((Date.now() - barStart) / MAX_RECORDING_MS) * 100, 100);
            setRecordingProgress(pct);
            if (pct >= 100 && recordingProgressIntervalRef.current) {
                clearInterval(recordingProgressIntervalRef.current);
                recordingProgressIntervalRef.current = null;
            }
        }, 100);
        // Lead-in delay so the user can start speaking before the highlight begins.
        replayHighlighting(wordBoundariesRef.current, 800);
    };

    const handleStopRecording = async () => {
        clearHighlightTimers();
        setActiveCharIndex(-1);
        if (recordingProgressIntervalRef.current) {
            clearInterval(recordingProgressIntervalRef.current);
            recordingProgressIntervalRef.current = null;
        }
        setRecordingProgress(0);
        if (!audioRecorderRef.current) return;
        audioRecorderRef.current.diag?.mark('tap_stop');
        const stopped = await audioRecorderRef.current.stop();
        if (stopped) { setRecordedAudioUrl(stopped.url); setRecordedAudioBlob(stopped.blob || null); }
        setIsRecording(false);
        if (stopped?.blob) {
            posthog.capture('phrase_recorded', { phrase_id: phrase?.id, source: 'practice' });
            await handleAnalyzeAudio(stopped.blob, stopped.mimeType, stopped.diag);
        }
    };

    const handlePlayRecording = () => {
        if (!recordedAudioUrl || !audioPlayerRef.current) return;
        if (isPlayingRecording) {
            audioPlayerRef.current.pause();
            audioPlayerRef.current.currentTime = 0;
            clearHighlightTimers();
            setActiveCharIndex(-1);
            setIsPlayingRecording(false);
        } else {
            replayHighlighting(wordBoundariesRef.current);
            setIsPlayingRecording(true);
            playRecording(audioPlayerRef.current, () => {
                clearHighlightTimers();
                setActiveCharIndex(-1);
                setIsPlayingRecording(false);
                toast.error(PLAYBACK_FAILED_MESSAGE);
            });
        }
    };

    const handleAnalyzeAudio = async (blob, mimeType, recDiag = null) => {
        if (!blob || !phrase) return;
        setAnalyzing(true);
        try {
            const res = await analyzeAudioApi.analyzeAudio(phrase.french, blob, mimeType, recDiag);
            const r = res.data;
            // "No speech detected" is a mic/recording problem, not pronunciation —
            // force the score to 0 (shown + saved) so it never reads as a real
            // attempt score. Same rule as Automatic.
            const silent = !!r.audio_issue && /no speech/i.test(r.audio_issue);
            if (silent) r.overall_score = 0;
            setResult(r);
            if (r.audio_issue) {
                const loud = await measureLoudness(blob);
                const props = {
                    error_type: silent ? 'no_speech_detected' : 'recording_issue',
                    mode: 'manual',
                    audio_issue: r.audio_issue,
                    loudness_rms: loud.rms,
                    loudness_peak: loud.peak,
                };
                // Quiet recordings are normal (iOS records ~50x lower), so they're a
                // plain analytics event, not an Error Tracking issue.
                if (silent) reportError('recording_silent', props);
                else posthog.capture('recording_quiet', props);
            }
            posthog.capture('feedback_received', { phrase_id: phrase.id, score: r.overall_score, match_quality: r.match_quality, source: 'practice', ...feedbackShownProps(phrase, r) });
            // Skip counting/saving recordings too quiet to score (overall_score == null):
            // they aren't real attempts and the backend save requires a numeric score.
            if (r.overall_score != null) {
                // A scored attempt is the moment the app has proved itself, so
                // it's what unlocks the add-to-home-screen prompt.
                markPractised();
                setPracticeCount(prev => prev + 1);
                if (!isLessonPhrase(phrase)) practiceApi.save({
                    phrase_id: phrase.id,
                    spoken_text: r.transcript_heard,
                    score: r.overall_score,
                    feedback: r.match_quality,
                    // Returned with the record so a sound can be tracked
                    // across the month, rather than read out once and binned.
                    sounds: r.sounds || null,
                    score_source: r.score_source || null,
                    word_scores: r.word_scores || null,
                    // No duration: practice minutes are timed while listening
                    // and speaking only (lib/practiceTime.js), in both modes.
                }).catch(err => console.warn('Failed to save practice record:', err));
            }
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
            console.error('Analyse error:', detail, err);
            reportError('analysis_failed',{ error_type: 'analysis_failed', mode: 'manual', clip_mime: mimeType, clip_bytes: blob.size, http_status: err?.response?.status, error: requestFailureReason(err) });
            toast.error(`Analysis failed: ${detail}`);
        } finally {
            setAnalyzing(false);
        }
    };

    // Swipe a card left to move on, before recording (a skip) or after it.
    // Only when there IS a next phrase: finishing the session is not something
    // to do by accident with a thumb. Never mid-recording either.
    const canAdvance = !singlePhraseId && currentIndex + 1 < phrases.length;
    // Swipe right, or the left arrow, goes back a phrase. Same rule.
    const canGoBack = !singlePhraseId && currentIndex > 0;
    const advanceSwipe = useSwipe({
        onSwipeLeft: canAdvance ? () => { if (!isRecording && !analyzing) handleNextPhrase(); } : null,
        onSwipeRight: canGoBack ? () => { if (!isRecording && !analyzing) handlePrevPhrase(); } : null,
    });

    const handleTryAgain = () => {
        setResult(null);
        setRecordedAudioUrl(null);
        setRecordedAudioBlob(null);
    };

    // Keep the latest score for the current phrase (retries overwrite).
    const recordCurrentResult = () => {
        if (!result || !phrase) return;
        // A recording that could not be scored (cut off, too quiet) still
        // counts as a phrase practised on the summary, with no score, and stays
        // out of the average. It used to be dropped, so 4 phrases read as 2.
        const entry = {
            phrase,
            score: result.overall_score,
            locked: !!result.score_locked,
            lockedMessage: result.locked_message || '',
            feedback: result.match_quality,
            issues: Array.isArray(result.issues) ? result.issues : [],
            phone_tips: Array.isArray(result.phone_tips) ? result.phone_tips : [],
            stress_tips: Array.isArray(result.stress_tips) ? result.stress_tips : [],
            word_scores: Array.isArray(result.word_scores) ? result.word_scores : [],
        };
        const idx = sessionResultsRef.current.findIndex(e => e.phrase.id === phrase.id);
        if (idx >= 0) sessionResultsRef.current[idx] = entry;
        else sessionResultsRef.current.push(entry);
    };

    const handleNextPhrase = () => {
        if (audioRecorderRef.current) audioRecorderRef.current.cleanup();
        recordCurrentResult();
        clearHighlightTimers();
        setActiveCharIndex(-1);
        // Reached the end of the list — finish the manual session.
        if (currentIndex + 1 >= phrases.length) {
            // Quick Practice (single phrase) just returns to the list.
            if (!singlePhraseId && sessionResultsRef.current.length > 0) {
                setSessionSummary(sessionResultsRef.current.slice());
            } else {
                toast.success('Manual practice complete!');
                navigate(returnTo);
            }
            return;
        }
        setResult(null);
        setRecordedAudioUrl(null);
        setRecordedAudioBlob(null);
        setCurrentIndex(i => i + 1);
    };

    // Back a phrase. Keeps this phrase's score for the summary, like moving on,
    // and opens the previous one fresh, ready to record again.
    const handlePrevPhrase = () => {
        if (currentIndex <= 0) return;
        if (audioRecorderRef.current) audioRecorderRef.current.cleanup();
        recordCurrentResult();
        clearHighlightTimers();
        setActiveCharIndex(-1);
        setResult(null);
        setRecordedAudioUrl(null);
        setRecordedAudioBlob(null);
        setCurrentIndex(i => i - 1);
    };

    const startRun = (list) => {
        if (!list || list.length === 0) return;
        if (audioRecorderRef.current) audioRecorderRef.current.cleanup();
        clearHighlightTimers();
        setActiveCharIndex(-1);
        sessionResultsRef.current = [];
        setSessionSummary(null);
        setResult(null);
        setRecordedAudioUrl(null);
        setRecordedAudioBlob(null);
        setPhrases(list);
        setCurrentIndex(0);
    };

    const handleRestart = () => startRun(allPhrasesRef.current);

    const handleRedoLowScores = () => {
        const low = (sessionSummary || []).filter(r => r.score != null && r.score < REDO_BELOW).map(r => r.phrase);
        const unique = [...new Map(low.map(p => [p.id, p])).values()];
        startRun(unique);
    };

    // Back button: confirm before leaving since a session is in progress.
    const handleBackClick = () => setShowLeaveConfirm(true);
    const confirmLeave = () => {
        setShowLeaveConfirm(false);
        if (audioRecorderRef.current) audioRecorderRef.current.cleanup();
        clearHighlightTimers();
        navigate(returnTo);
    };

    // Order a phrase list the same way fetchPhrases does (smart vs random).
    const orderPhrases = (list) => {
        const unique = [...new Map(list.map(p => [p.id, p])).values()];
        if (order === 'random') {
            for (let i = unique.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [unique[i], unique[j]] = [unique[j], unique[i]];
            }
        } else {
            unique.sort((a, b) => {
                const aNever = (a.practice_count || 0) === 0;
                const bNever = (b.practice_count || 0) === 0;
                if (aNever !== bNever) return aNever ? -1 : 1;
                const scoreDiff = (a.average_score || 0) - (b.average_score || 0);
                if (scoreDiff !== 0) return scoreDiff;
                const aLast = a.last_practiced || '';
                const bLast = b.last_practiced || '';
                return aLast < bLast ? -1 : aLast > bLast ? 1 : 0;
            });
        }
        return unique;
    };

    // Pick a different collection from the end-of-session screen and start a fresh run.
    const startCollectionSession = async (collectionId) => {
        try {
            const res = collectionId === '__all__'
                ? await phrasesApi.getAll()
                : await phrasesApi.getAll(collectionId);
            const ordered = orderPhrases(res.data);
            if (ordered.length === 0) {
                toast.error('No phrases in that collection.');
                return;
            }
            setCollectionLabel(collectionId === '__all__'
                ? 'All Phrases'
                : (collections.find(c => c.id === collectionId)?.name || ''));
            allPhrasesRef.current = ordered;
            startRun(ordered);
        } catch {
            toast.error('Failed to load that collection.');
        }
    };

    if (typeof MediaRecorder === 'undefined') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-foreground px-6">
                <div className="text-center max-w-sm space-y-4">
                    <p className="text-2xl">⚠️</p>
                    <h1 className="text-lg font-semibold text-primary-foreground">Your iOS version is out of date</h1>
                    <p className="text-primary-foreground/70 text-sm leading-relaxed">
                        Manual mode needs a feature that isn't available in older versions of iOS.
                    </p>
                    <p className="text-primary-foreground/70 text-sm leading-relaxed">
                        To fix this, go to <strong className="text-primary-foreground">Settings → General → Software Update</strong> and update to iOS 15 or later.
                    </p>
                    <button
                        onClick={() => navigate(returnTo)}
                        className="mt-4 text-sm text-accent underline"
                    >
                        Go back
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-foreground">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-primary-foreground/70">Loading phrase...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-fixed bg-gradient-to-b from-foreground to-primary text-primary-foreground" data-testid="practice-page">
            {/* Hidden audio player for recorded voice */}
            {recordedAudioUrl && (
                <audio
                    ref={audioPlayerRef}
                    src={recordedAudioUrl}
                    onEnded={() => { clearHighlightTimers(); setActiveCharIndex(-1); setIsPlayingRecording(false); }}
                />
            )}

            {/* Header */}
            <SessionHeader title={collectionLabel || (singlePhraseId ? 'Quick Practice' : 'All Phrases')}>
                <Badge variant="outline" className="border-accent text-accent bg-transparent" data-testid="manual-flow-tab">
                    <Pointer className="h-3 w-3 mr-1" /> Manual
                </Badge>
                {!singlePhraseId && (
                    <Badge variant="outline" className="border-primary-foreground/40 text-primary-foreground bg-transparent" data-testid="manual-order-badge">
                        {order === 'random' ? <Shuffle className="h-3 w-3 mr-1" /> : <Target className="h-3 w-3 mr-1" />}
                        {order === 'random' ? 'Random' : order === 'added' ? 'Order added' : 'Smart'}
                    </Badge>
                )}
            </SessionHeader>

            <main className="max-w-3xl mx-auto px-6 py-4">
                {sessionSummary ? (
                    (() => {
                        const lowScores = sessionSummary.filter(r => r.score != null && r.score < REDO_BELOW);
                        return (
                            <>
                            <Card className="bg-white border-stone-100 shadow-lg animate-fade-in" data-testid="manual-summary">
                                <CardContent className="p-8">
                                    <SessionSummary results={sessionSummary} challengeDay={user?.challenge_day} challengeUserName={user?.name} />
                                </CardContent>
                            </Card>

                                    <div className="flex flex-wrap gap-4 justify-center mt-8">
                                        {lowScores.length > 0 && (
                                            <Button
                                                data-testid="manual-redo-low-btn"
                                                className="rounded-full px-5 bg-yellow-500 hover:bg-yellow-600 text-white text-xs shadow-md"
                                                onClick={handleRedoLowScores}
                                            >
                                                <RotateCcw className="h-4 w-4 mr-1" />
                                                Redo Low Scores
                                            </Button>
                                        )}
                                        <Button
                                            data-testid="manual-restart-btn"
                                            className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground text-xs shadow-md px-5"
                                            onClick={handleRestart}
                                        >
                                            <RotateCcw className="h-4 w-4 mr-1" />
                                            Restart Session
                                        </Button>
                                        <Select onValueChange={startCollectionSession}>
                                            <SelectTrigger
                                                data-testid="manual-other-collection-select"
                                                className="rounded-full border-stone-300 px-6 w-auto gap-2 bg-transparent text-foreground"
                                            >
                                                <Folder className="h-4 w-4" />
                                                <SelectValue placeholder="Practice different collection" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white text-foreground">
                                                <SelectItem value="__all__">All Phrases</SelectItem>
                                                <CollectionOptions>
                                                    {collections.map(c => (
                                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                                    ))}
                                                </CollectionOptions>
                                            </SelectContent>
                                        </Select>
                                        <Button
                                            data-testid="manual-summary-back-btn"
                                            variant="outline"
                                            className="rounded-full border-stone-300 px-6"
                                            onClick={() => navigate(returnTo)}
                                        >
                                            <ArrowLeft className="h-4 w-4 mr-1" />
                                            Back to {returnTo === '/collections' ? 'Phrases' : 'Dashboard'}
                                        </Button>
                                    </div>
                            </>
                        );
                    })()
                ) : (
                <>
                {/* Progress — phrase count + overall completion */}
                {!singlePhraseId && phrases.length > 0 && (
                    <div className="mb-3">
                        <div className="flex justify-between text-xs text-primary-foreground/70 mb-1">
                            <span>Phrase {currentIndex + 1} of {phrases.length}</span>
                            <span>{Math.round(((currentIndex + (result ? 1 : 0)) / phrases.length) * 100)}%</span>
                        </div>
                        <Progress
                            value={((currentIndex + (result ? 1 : 0)) / phrases.length) * 100}
                            className="h-2"
                            data-testid="manual-progress"
                        />
                    </div>
                )}

                {/* Phrase Card — hidden once scored so the result shows at the top */}
                {!result && (
                <PhraseCard {...advanceSwipe}>
                        {/* Exit — plain red X, hard against the corner. Always shown,
                            including Quick Practice, so there's a way out from anywhere.
                            For singlePhraseId we go straight back (no leave-confirm
                            dialog — nothing to lose). */}
                        <button
                            type="button"
                            data-testid="manual-exit-x-btn"
                            onClick={singlePhraseId ? () => navigate(returnTo) : handleBackClick}
                            aria-label="Exit"
                            title="Exit"
                            className="absolute top-0 right-0 z-10 inline-flex items-center justify-center w-8 h-8 text-stone-400 hover:text-stone-600"
                        >
                            <X className="h-5 w-5" strokeWidth={2.5} />
                        </button>
                        {/* Phrase — English on top, French below, phonetics on demand */}
                        <PhraseDisplay
                            phrase={phrase}
                            activeCharIndex={activeCharIndex}
                            karaokeEnabled={karaokeEnabled}
                            className="mb-4"
                        />

                        {/* Listen Button — sits right under the phrase */}
                        <div className="flex justify-center mb-6">
                            <Button
                                data-testid="listen-btn"
                                size="lg"
                                variant="outline"
                                className="rounded-full border-2 border-accent text-accent hover:bg-accent hover:text-accent-foreground px-5 md:px-8"
                                onClick={handlePlayAudio}
                                disabled={isPlaying}
                            >
                                <Volume2 className={`h-5 w-5 mr-1 ${isPlaying ? 'animate-pulse' : ''}`} />
                                {isPlaying ? 'Playing...' : 'Listen to Native'}
                            </Button>
                        </div>

                        {/* Recording Section */}
                        <div className="text-center">
                            <p className="text-xs text-stone-400 mb-4">Now, try saying it:</p>

                            {/* Record Button */}
                            <div className="flex justify-center mb-4">
                                {!isRecording ? (
                                    <Button
                                        data-testid="record-btn"
                                        size="lg"
                                        className={`rounded-full w-24 h-24 ${result ? 'bg-stone-400' : 'bg-accent hover:bg-accent/80'} shadow-xl transition-all hover:scale-105`}
                                        onClick={handleStartRecording}
                                        disabled={!!result || analyzing}
                                    >
                                        <Mic className="h-10 w-10" />
                                    </Button>
                                ) : (
                                    <Button
                                        data-testid="stop-record-btn"
                                        size="lg"
                                        className="rounded-full w-24 h-24 bg-rose-500 hover:bg-rose-600 shadow-xl animate-pulse-record"
                                        onClick={handleStopRecording}
                                    >
                                        <MicOff className="h-10 w-10" />
                                    </Button>
                                )}
                            </div>

                            {/* Speed control — under the record button */}
                            <SpeedControl value={playbackSpeed} onChange={handleSpeedChange} className="mb-6" />

                            {/* Recording Status */}
                            {isRecording && (
                                <div className="mb-6">
                                    <div className="flex justify-center gap-1 mb-3">
                                        {[1,2,3,4,5].map(i => (
                                            <div key={i} className="w-1 bg-accent rounded-full wave-bar"></div>
                                        ))}
                                    </div>
                                    <p className={`text-xs mb-2 font-medium ${recordingProgress > 80 ? 'text-amber-600' : 'text-stone-400'}`}>
                                        {recordingProgress > 80 ? 'Wrap up now' : 'Recording... Speak now!'}
                                    </p>
                                    <div className="w-full max-w-[220px] mx-auto bg-stone-200 rounded-full h-1.5 overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-none ${recordingProgress > 80 ? 'bg-amber-500' : 'bg-rose-400'}`}
                                            style={{ width: `${recordingProgress}%` }}
                                        />
                                    </div>
                                    <p className="text-[10px] text-stone-400 text-center mt-1">30-second limit</p>
                                </div>
                            )}
                            {autoStoppedNotice && !isRecording && (
                                <p className="text-xs text-amber-600 text-center mb-4 px-4" data-testid="auto-stopped-notice">
                                    Your recording hit the 30-second limit and was stopped automatically. Try a shorter phrase for the most accurate score.
                                </p>
                            )}

                            {/* Skip. Nothing says a phrase has to be recorded to
                                be left. The same green arrow as every other card
                                (the "Skip this phrase" link read as inconsistent,
                                Leanne, 27 Oct 2026); swiping left does the same.
                                Hidden while recording or analysing, where it
                                would mean throwing away the take in progress. */}
                            {canAdvance && !isRecording && !analyzing && (
                                <NextPhraseButton testId="manual-skip-btn" label="Skip this phrase"
                                                  onClick={handleNextPhrase} />
                            )}
                            {canGoBack && !isRecording && !analyzing && (
                                <NextPhraseButton back testId="manual-prev-btn" onClick={handlePrevPhrase} />
                            )}

                            {/* Analysing State */}
                            {analyzing && !result && (
                                <div className="mb-6 animate-fade-in text-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-accent mx-auto mb-3" />
                                    <p className="text-xs text-stone-400">Analysing your pronunciation...</p>
                                </div>
                            )}
                        </div>
                </PhraseCard>
                )}

                {/* Results Card */}
                {result && (
                    <Card className="bg-white border-stone-100 shadow-lg animate-fade-in relative" data-testid="result-card"
                          {...advanceSwipe}>
                        {/* Exit — red X, hard against the corner. Always shown so Quick
                            Practice also has a visible way out; there it goes straight
                            back with no leave-confirm dialog. */}
                        <button
                            type="button"
                            data-testid="manual-result-exit-x-btn"
                            onClick={singlePhraseId ? () => navigate(returnTo) : handleBackClick}
                            aria-label="Exit"
                            title="Exit"
                            className="absolute top-0 right-0 z-10 inline-flex items-center justify-center w-8 h-8 text-stone-400 hover:text-stone-600"
                        >
                            <X className="h-5 w-5" strokeWidth={2.5} />
                        </button>
                        {/* Next Phrase — chevron button on the right edge of the card,
                            always visible without scrolling. Skipped for Quick Practice
                            (single-phrase runs). */}
                        {!singlePhraseId && currentIndex + 1 < phrases.length && (
                            <NextPhraseButton testId="manual-next-side-btn" onClick={handleNextPhrase} />
                        )}
                        {canGoBack && (
                            <NextPhraseButton back testId="manual-prev-side-btn" onClick={handlePrevPhrase} />
                        )}
                        {/* Extra top-right and right padding reserves space for the
                            absolute-positioned X (top-right) and Next chevron (mid-right)
                            so content can't run under them. */}
                        <CardContent className="p-8 pt-10 pr-10 pl-10">
                            {/* Out of scored recordings. The practice still happened
                                and the screen still works; only the number is behind
                                the padlock. */}
                            {/* Score, label and what it means: one component shared with
                                Automatic, so the two cannot differ (ScoreHeader). */}
                            <ScoreHeader score={result.overall_score} locked={!!result.score_locked}
                                         onUpgrade={() => setUpgradePrompt(result.locked_message)} />

                            {/* Order, same as Automatic (Leanne, 21 Oct 2026): score,
                                Feedback box, "Tap any word", then the phrase. */}
                            {/* Recording-quality notice — shown instead of pronunciation feedback */}
                            {result.audio_issue ? (
                                <RecordingNotice audioIssue={result.audio_issue} score={result.overall_score} mode="manual" />
                            ) : result.score_locked ? null : (
                            /* No Feedback box without a score: nothing was
                               graded, and the fallback sentence for an empty
                               result read "Significant differences from the
                               target phrase", telling a free user they had
                               got it wrong. */
                            /* Feedback — prefer the model's specific feedback; the
                               match_quality sentence is only a fallback so a 100 and a
                               95 don't read identically. */
                            <FeedbackBox className="mb-3">{result.feedback?.trim() || {
                                    exact: 'Spot on! Your pronunciation closely matches the target.',
                                    close: 'Very close, just a few minor adjustments needed.',
                                    partial: 'Good attempt, some sounds need work.',
                                    mismatch: 'Significant differences from the target phrase.',
                                }[result.match_quality] || 'Analysis complete.'}</FeedbackBox>
                            )}
                            {pronunciationBeta() && !result.score_locked && <TapWordPrompt />}

                            {/* Comparison with Playback */}
                            <div className="grid md:grid-cols-2 gap-4 mb-6">
                                <div className="order-3 md:order-1 p-4 bg-stone-50 rounded-xl">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs text-stone-500 flex items-center gap-1">
                                            <Check className="h-3 w-3 text-emerald-500" /> Target
                                        </p>
                                        <Button
                                            data-testid="play-native-btn"
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 px-2"
                                            onClick={handlePlayAudio}
                                            disabled={isPlaying}
                                        >
                                            <Volume2 className="h-3 w-3 mr-1" />
                                            {isPlaying ? 'Playing' : 'Play'}
                                        </Button>
                                    </div>
                                    {/* Plain here. The colours belong on Your voice
                                        below: it is their pronunciation being
                                        scored, and it puts the words within reach
                                        of the tip that explains them. */}
                                    <p className="text-sm text-foreground font-medium leading-relaxed">
                                        {phrase?.french || ''}
                                    </p>
                                    {/* The English stays on the results, as in Automatic. */}
                                    {phrase?.english && (
                                        <p className="text-xs text-stone-500 mt-1" data-testid="result-english-text">{phrase.english}</p>
                                    )}
                                </div>
                                <div className="order-1 md:order-2 p-4 bg-stone-50 rounded-xl">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs text-stone-500 flex items-center gap-1">
                                            <Mic className="h-3 w-3 text-accent" /> Your voice
                                        </p>
                                        {recordedAudioUrl && (
                                            <Button
                                                data-testid="play-recording-result-btn"
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2"
                                                onClick={handlePlayRecording}
                                            >
                                                <PlayCircle className="h-3 w-3 mr-1" />
                                                {isPlayingRecording ? 'Stop' : 'Play'}
                                            </Button>
                                        )}
                                    </div>
                                    {/* The scored words, here rather than on Target.
                                        The words are the target's, because that is
                                        what each score is measured against, but this
                                        is the panel about their attempt and it sits
                                        next to the tips. */}
                                    <ScoredPhrase phrase={phrase?.french || ''}
                                                  wordScores={result.word_scores || []}
                                                  wordPickable
                                                  scoreLocked={!!result.score_locked}
                                                  phoneTips={result.phone_tips || []}
                                                  stressTips={result.stress_tips || []}
                                                  issues={result.issues || []}
                                                  footerTarget={wordFooter}
                                                  missedWords={result.missed_words || []}
                                                  missedAtEnd={!!result.missed_at_end}
                                                  missedAtStart={!!result.missed_at_start}
                                                  missedAdvice="stop"
                                                  missedDetail={result.missed_detail || []}
                                                  recordedAudio={recordedAudioBlob || recordedAudioUrl} />
                                </div>
                                {/* The picked-word controls land here, via a portal from ScoredPhrase.
                                    Inside the panel they pushed the "Your voice" phrase 78px down a phone.
                                    On a phone the order is Your voice, these controls, then Target, so the
                                    highlighted words and their tips are on screen together. */}
                                <div ref={setWordFooter} className="order-2 md:order-3 md:col-span-2 empty:hidden" />
                            </div>

                            {/* Coaching tips — Speechace's real phoneme + stress data
                                first, Gemini's guessed issues as a fallback when
                                Speechace didn't run. Shown even on a cut-off recording:
                                Speechace can still return useful tips for the words it
                                did hear, and the learner benefits from seeing them. */}
                            {/* Beta: no separate list. Tapping a highlighted word in the
                                phrase above shows its card, one feature not two. */}
                            {!pronunciationBeta() && (
                            <div className="mb-8">
                                <PronunciationTips
                                    phoneTips={result.phone_tips || []}
                                    stressTips={result.stress_tips || []}
                                    issues={result.issues || []}
                                    scoreLocked={!!result.score_locked}
                                    phrase={phrase?.french || ''}
                                    wordScores={result.word_scores || []}
                                    recordedAudio={recordedAudioBlob || recordedAudioUrl}
                                />
                            </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Action Buttons — Try Again always sits below the result. Exit
                    and Next Phrase now live in the result card's corners so this
                    row stays uncluttered. For a Quick Practice (single-phrase)
                    run there's no "next", so a Back-to-Phrases button lives here
                    alongside Try Again. At the last phrase of a session we also
                    surface Finish here since the side chevron is hidden. */}
                {result && (
                            <div className="flex flex-wrap gap-4 justify-center mt-8">
                                <TryAgainButton onClick={handleTryAgain} />
                                {singlePhraseId && (
                                    <Button
                                        data-testid="back-to-phrases-btn"
                                        className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground text-xs shadow-md px-5"
                                        onClick={() => navigate(returnTo)}
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-1" />
                                        Back to {returnTo === '/collections' ? 'Phrases' : 'Dashboard'}
                                    </Button>
                                )}
                                {/* Next, beside Try Again. The chevron on the card's
                                    edge is out of sight on a long result, and
                                    hunting for it after every phrase is work. */}
                                {!singlePhraseId && currentIndex + 1 < phrases.length && (
                                    <Button
                                        data-testid="next-phrase-bottom-btn"
                                        className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground text-xs shadow-md px-5"
                                        onClick={handleNextPhrase}
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                )}
                                {!singlePhraseId && currentIndex + 1 >= phrases.length && (
                                    <Button
                                        data-testid="next-phrase-btn"
                                        className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground text-xs shadow-md px-5"
                                        onClick={handleNextPhrase}
                                    >
                                        <Check className="h-4 w-4 mr-1" />
                                        Finish
                                    </Button>
                                )}
                            </div>
                )}
                </>
                )}
            </main>

            <AlertDialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Stop this session?</AlertDialogTitle>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="leave-cancel-btn">No, keep practicing</AlertDialogCancel>
                        <AlertDialogAction data-testid="leave-confirm-btn" onClick={confirmLeave}>
                            Yes, go back to {returnTo === '/collections' ? 'Collections' : 'Dashboard'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Free allowance spent. Same dialog as every other paid wall in
                the app, carrying the server's own wording. */}
            <UpgradeDialog
                open={!!upgradePrompt}
                onOpenChange={(open) => !open && setUpgradePrompt(null)}
                title="Pronunciation scoring is a paid feature"
                message={upgradePrompt}
                showOpinionBox
            />
        </div>
    );
};

export default Manual;
